import { CloseIcon } from '@fabric-app/icons/icons';
import { Button } from '@fabric-app/ui/components/Button';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Image, ImageElement } from '@fabric-app/ui/components/Image';
import { mediaHover } from '@fabric-app/ui/constants/styled-utils';
import isHotkey from 'is-hotkey';
import styled, { css } from 'styled-components';

type SearchImage = {
  src: string;
  onRemove: () => void;
};

const RemoveButton = styled(Button).attrs({
  shape: 'round',
  size: 'xss',
  variant: 'bg-primary',
})`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const buttonActive = css`
  outline: none;

  ${ImageElement} {
    opacity: 0.5;
  }

  ${RemoveButton} {
    opacity: 1;
    pointer-events: auto;
  }
`;

const Container = styled(Flex).attrs({
  justifyContent: 'center',
  alignItems: 'center',
})`
  position: relative;
  width: auto;
  height: 41px;
  max-width: 60px;
  border-radius: 2px;
  overflow: hidden;

  ${ImageElement} {
    transition: opacity 0.2s;

    width: auto;
  }

  &:focus-visible {
    outline: 4px solid rgba(128, 128, 128, 0.2); // gray so it works on dark mode
  }

  // separate hover so clicking the center on mobile won't instantly remove the image
  ${mediaHover} {
    :hover {
      ${buttonActive}
    }
  }

  &:focus,
  &:focus-within {
    ${buttonActive}
  }
`;

const SearchImage: React.FC<SearchImage> = ({ src, onRemove }) => {
  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isHotkey('enter', e) && !isHotkey('space', e)) {
      return;
    }

    onRemove();
  };

  return (
    <Container tabIndex={0} onKeyDown={onKeyDownHandler}>
      <Image src={src} alt="Search Image" fullHeight />
      <RemoveButton onClick={onRemove} tabIndex={-1}>
        <CloseIcon
          style={{
            width: 12,
            height: 12,
          }}
        />
      </RemoveButton>
    </Container>
  );
};

export default SearchImage;
